import { addError } from "@wayflyer/blackbox-fe";
import { WayflyerSchemas } from "api-types";
import { useAxios } from "auth-sdk";
import { isAxiosError } from "axios";
import { createContext, useCallback, useContext, useState } from "react";
import { useAnalyticsContext } from "shared-utils";
import type { AxiosInstance } from "axios";

import { analytics } from "../analytics";
import type { ModalConfig } from "../components/invite-modal/types";

export type InviteContextValue = {
  inviteHandler: (email: string, roles?: WayflyerSchemas.CompanyRole[], onSuccess?: () => void) => Promise<void>;
  isModalOpen: boolean;
  openModal: (
    onCloseCallback?: () => void,
    onOpenCallback?: () => void,
    onSuccessCallback?: () => void,
    modalConfig?: ModalConfig
  ) => void;
  closeModal: () => void;
  onSuccessCallback?: (() => void) | undefined;
  modalConfig?: ModalConfig;
};

export interface InviteContextProps {
  customerSlug: string;
  children: any;
}

const InviteContext = createContext<InviteContextValue>({
  inviteHandler: () => Promise.reject("Something went wrong, please contact support"),
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
});

const errorMap = {
  "Email already exists": "Invite - Invitation error - Email already exists",
  "Rate limit reached": "Invite - Invitation error - Rate limit reached",
  "internal server error": "Something went wrong, please contact support",
  "Invalid email": "Invite - Invitation error - Invalid email",
};

const sendInvite = async (
  client: AxiosInstance,
  customerSlug: string,
  email: string,
  roles?: WayflyerSchemas.CompanyRole[]
) => {
  try {
    if (!customerSlug.length) {
      throw Error("No customerSlug available when attempting to send invite to: " + email);
    }
    await client.post(`/api/${customerSlug}/invite/`, { email, roles });
  } catch (error) {
    addError(error);
    const message = isAxiosError(error)
      ? (error.response?.data as { message?: unknown } | undefined)?.message
      : undefined;
    throw (typeof message === "string" && errorMap[message]) || "Invite - Invitation error";
  }
};

export function InviteProvider({ customerSlug, children }: InviteContextProps) {
  const client = useAxios();
  const analyticsContext = useAnalyticsContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | undefined>();
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>();
  const [modalConfig, setModalConfig] = useState<ModalConfig | undefined>(undefined);

  const inviteHandler = useCallback(
    (email: string, roles?: WayflyerSchemas.CompanyRole[], onSuccess?: () => void) => {
      analytics.inviteMembersSendClicked(analyticsContext);
      return sendInvite(client, customerSlug, email, roles).then(() => {
        if (onSuccess) {
          onSuccess();
        }
      });
    },
    [analyticsContext, client, customerSlug]
  );

  const openModal = useCallback(
    (
      onCloseCallback?: () => void,
      onOpenCallback?: () => void,
      onSuccessCallback?: () => void,
      modalConfig?: ModalConfig
    ) => {
      if (onOpenCallback) {
        onOpenCallback();
      }
      setOnCloseCallback(() => onCloseCallback);
      setOnSuccessCallback(() => onSuccessCallback);
      setModalConfig(modalConfig);
      setIsModalOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(undefined);
    }
    setOnSuccessCallback(undefined);
    setModalConfig(undefined);
  }, [onCloseCallback]);

  return (
    <InviteContext.Provider
      value={{
        inviteHandler,
        isModalOpen,
        openModal,
        closeModal,
        onSuccessCallback,
        modalConfig,
      }}
    >
      {typeof children === "function" ? children({ inviteHandler }) : children}
    </InviteContext.Provider>
  );
}

export const useInviteHandler = (): InviteContextValue => useContext(InviteContext);

export const useInviteModal = () => {
  const context = useContext(InviteContext);
  return {
    isOpen: context.isModalOpen,
    openModal: context.openModal,
    closeModal: context.closeModal,
    modalConfig: context.modalConfig,
  };
};

export const useInviteModalActions = () => {
  const { openModal, closeModal } = useInviteModal();

  return {
    openInviteModal: openModal,
    closeInviteModal: closeModal,
  };
};
